import { useContext, useState } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useNextT } from "../i18n";
import { guardedLeave, hasUnsavedChanges, VISUALLY_HIDDEN } from "../ui";
import { useProblemsNewCount } from "../console/staff/problems/api";
import { LegacyExportDataModal } from "../bridges/frame";
import { Icon } from "./icons";
import { matchNav } from "./nav";
import { useNav } from "./useNav";
import s from "./SideMenu.module.scss";

/**
 * onChoose (phone drawer): the menu hands each choice to the frame instead of acting at once, so the drawer can
 * close first and the choice runs after it has gone. Navigating in the same update as the close made the drawer
 * stay on screen until a heavy page (Contacts, Map) had finished drawing.
 */
/** P2: the Problems item's red number — open problems in since this person last opened the page (only where the item shows). */
const ProblemsCount = () => (useContext(QueryClientContext) ? <ProblemsCountShown /> : null);
const ProblemsCountShown = () => {
  const { t } = useNextT();
  const n = useProblemsNewCount(true).data ?? 0;
  if (n <= 0) return null;
  return <><span className={s.badge} aria-hidden="true">{n > 99 ? "99+" : n}</span><span className={VISUALLY_HIDDEN}> · {t("shell.nav.problems_new", { n })}</span></>;
};

export const SideMenu = ({ onChoose }: { onChoose?(go: () => void): void }) => {
  const navigate = useNavigate();
  const { t } = useNextT();
  const { pathname } = useLocation();
  const { items, showAdmin, setShowAdmin, canSwitch } = useNav();
  const [modal, setModal] = useState<"export" | null>(null);
  const current = matchNav(items, pathname);

  return (
    <nav className={s.menu} aria-label={t("shell.nav.label")}>
      {showAdmin && <div className={s.heading}>{t("shell.nav.admin_heading")}</div>}
      <ul className={s.list}>
        {items.map((it) => (
          <li key={it.key}>
            {it.to ? (
              <Link to={it.to} className={s.item} aria-current={current?.key === it.key ? "page" : undefined}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // let "open in new tab" work
                  if (!onChoose && !hasUnsavedChanges()) return; // a plain link
                  e.preventDefault();
                  const to = it.to!;
                  // Unsaved changes on the page: its "Leave without saving?" asks first (after the drawer has closed).
                  const go = () => guardedLeave(() => navigate(to));
                  if (onChoose) onChoose(go); else go();
                }}>
                <Icon name={it.key} /><span>{t(`shell.nav.${it.label ?? it.key}`)}</span>
                {it.key === "problems" && <ProblemsCount />}
              </Link>
            ) : (
              <button type="button" className={s.item} onClick={() => { const open = () => setModal(it.action!); if (onChoose) onChoose(open); else open(); }}>
                <Icon name={it.key} /><span>{t(`shell.nav.${it.label ?? it.key}`)}</span>
              </button>
            )}
          </li>
        ))}
      </ul>
      <div className={s.grow} />
      {canSwitch && (
        <button type="button" className={s.switch} onClick={() => setShowAdmin(!showAdmin)}>
          <Icon name="swap" />{t(showAdmin ? "shell.nav.to_main" : "shell.nav.to_admin")}
        </button>
      )}
      <LegacyExportDataModal open={modal === "export"} onClose={() => setModal(null)} />
    </nav>
  );
};
